/**
 * Company-profile questions on the /book-demo lead form (#4620).
 *
 * These exist so the Jev qualification screen can tell a SaaS or funded company with
 * a real outbound motion from a tiny consumer-services business. It used to decide
 * from company name, headcount and title alone, so it could not tell them apart.
 * Pounce forwards the whole form to Jev, so a new field only has to reach
 * `formData` to be seen.
 *
 * Every option string here is ALSO the option value of the matching HubSpot contact
 * property in portal 242790028 (`business_model`, `funding_stage`, `sales_team_size`,
 * created 2026-09-24 with value === label). The same string can go to Pounce and to
 * HubSpot with no mapping table. HubSpot rejects an enumeration value it does not
 * know, so editing a string here without editing the property drops the field from
 * the CRM record.
 */

export const BUSINESS_MODEL_OPTIONS = [
  'Software / SaaS',
  'Tech-enabled services',
  'Other B2B products or services',
  'Consumer / B2C',
  'Agency / consultancy',
] as const;

export const FUNDING_STAGE_OPTIONS = [
  'Bootstrapped / profitable',
  'Pre-seed / Seed',
  'Series A',
  'Series B+',
  'PE-backed',
  'Public',
  'Prefer not to say',
] as const;

export const SALES_TEAM_SIZE_OPTIONS = [
  'Just me / founder-led',
  '2-5',
  '6-20',
  '21-50',
  '50+',
] as const;

/**
 * The website to prefill from a work email: its domain, or '' while the email is
 * still incomplete. Free-mail domains are not special-cased because Pounce rejects
 * those addresses before qualification anyway.
 */
export const websiteFromEmail = (email: string): string => {
  const domain = email.trim().toLowerCase().split('@')[1] || '';
  return /^[^\s@]+\.[a-z]{2,}$/.test(domain) ? domain : '';
};

/**
 * Loose check that a value could be a company website: a hostname with a dot and a
 * TLD, with or without a scheme or path. It only has to catch typos and "n/a".
 * Deciding whether the company is real is Jev's job, not the form's.
 */
export const isPlausibleWebsite = (value: string): boolean => (
  /^(https?:\/\/)?[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}(\/\S*)?$/i.test(value.trim())
);
